import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, ArrowLeft, Loader, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import Logo from '../../components/common/Logo'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isOtpSent, setIsOtpSent] = useState(false)
  const [otpsId, setOtpsId] = useState(null)
  const [otp, setOtp] = useState('')
  const [mobilePhone, setMobilePhone] = useState(null)
  const [mobileOtp, setMobileOtp] = useState('')
  const [showMobileOtp, setShowMobileOtp] = useState(false)
  const [mobileOtpsId, setMobileOtpsId] = useState(null)
  const [isEmailVerified, setIsEmailVerified] = useState(false)
  const [isMobileVerified, setIsMobileVerified] = useState(false)
  const otpRef = useRef(null)

  useEffect(() => {
    if (isOtpSent) {
      // focus OTP input when it appears
      setTimeout(() => otpRef.current?.focus(), 50)
    }
  }, [isOtpSent])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Call API to send OTP / reset
      const res = await (await import('../../services/authService')).authService.forgotPassword(email)
      if (res && res.success) {
        setOtpsId(res.data?.otps_id || null)
        setIsOtpSent(true)
        toast.success(res.message || 'OTP sent to your email')
      } else {
        toast.error(res.message || 'Failed to send OTP. Please try again.')
      }
    } catch (error) {
      console.error('Forgot password error', error)
      toast.error(error.message || 'Failed to send OTP. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const navigate = useNavigate()

  const handleVerifyOtp = async () => {
    if (!otp || !otpsId) {
      toast.error('Please enter the OTP')
      return
    }

    setIsLoading(true)
    try {
  const { authService } = await import('../../services/authService')
  // Use company-specific verify endpoint for forgot-password flow
  const res = await authService.verifyEmailOtpForReset({ email, otp, otps_id: otpsId })
      if (res && res.success) {
        toast.success(res.message || 'Email OTP verified')
        // mark email OTP as verified when backend confirms
        setIsEmailVerified(true)
        // If backend provides a phone to verify next, show mobile OTP input
        const userPhone = res.data?.user_phone || res.data?.userPhone || res.data?.phone
        const nextMobileOtpsId = res.data?.mobile_otps_id || res.data?.mobileOtpsId || null
        if (userPhone) {
          setMobilePhone(userPhone)
          setMobileOtpsId(nextMobileOtpsId)
          setShowMobileOtp(true)
          // focus handled by effect when showMobileOtp becomes true
        } else {
          // If API returned a token, redirect to reset password with token
          const token = res.data?.token || res.data?.access_token
          if (token) {
            navigate(`/auth/reset-password?token=${encodeURIComponent(token)}`)
          } else {
            // Pass email and otps_id so reset page can call the PATCH endpoint
            const redirectUrl = `/auth/reset-password?email=${encodeURIComponent(email)}${otpsId ? `&otps_id=${encodeURIComponent(otpsId)}` : ''}`
            navigate(redirectUrl)
          }
        }
      } else {
        toast.error(res.message || 'OTP verification failed')
      }
    } catch (err) {
      console.error('OTP verify error', err)
      toast.error(err.message || 'OTP verification failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyMobileOtp = async () => {
    if (!mobileOtp || !mobilePhone) {
      toast.error('Please enter the mobile OTP')
      return
    }
    setIsLoading(true)
    try {
      const { authService } = await import('../../services/authService')
      const res = await authService.verifyPhoneOtpForReset({ phone: mobilePhone, otp: mobileOtp, otps_id: mobileOtpsId || otpsId })
      if (res && res.success) {
        toast.success(res.message || 'Mobile OTP verified')
        // mark mobile OTP as verified
        setIsMobileVerified(true)
        const token = res.data?.token || res.data?.access_token
        if (token) {
          navigate(`/auth/reset-password?token=${encodeURIComponent(token)}`)
        } else {
          const usedOtpsId = mobileOtpsId || otpsId
          const redirectUrl = `/auth/reset-password?email=${encodeURIComponent(email)}${usedOtpsId ? `&otps_id=${encodeURIComponent(usedOtpsId)}` : ''}`
          navigate(redirectUrl)
        }
      } else {
        toast.error(res.message || 'Mobile OTP verification failed')
      }
    } catch (err) {
      console.error('Mobile OTP verify error', err)
      toast.error(err.message || 'Mobile OTP verification failed')
    } finally {
      setIsLoading(false)
    }
  }

  // If OTP sent, show inline OTP input instead of navigating to a separate page
  // We intentionally keep user on the same page to enter OTP

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm"
      >
        <div className="card p-6 shadow-xl space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="w-10 h-10">
                <Logo sizeClass="w-10 h-10" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Forgot password?</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Enter your email to reset your password
            </p>
          </div>

          {/* Form */}
          {!isOtpSent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="email" className="label text-xs">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-9 py-2 text-sm"
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email}
                className="btn btn-primary w-full py-2.5 shadow-lg shadow-primary-500/20 group text-sm"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send OTP
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">We sent an OTP to <strong>{email}</strong>. Enter it below to continue.</p>

              <div className="space-y-1.5">
                <label htmlFor="otp" className="label text-xs">Enter OTP</label>
                <div className="relative">
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    ref={otpRef}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyOtp() } }}
                    className="input py-2 text-sm pr-10"
                    placeholder="Enter OTP"
                    disabled={isEmailVerified}
                  />
                  {isEmailVerified && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center text-success-600">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                  {!isEmailVerified ? (
                    <button
                      onClick={handleVerifyOtp}
                      disabled={isLoading || !otp}
                      className="btn btn-primary flex-1 py-2.5 text-sm"
                    >
                      {isLoading ? <Loader className="w-4 h-4 animate-spin mr-2 inline" /> : 'Verify OTP'}
                    </button>
                  ) : (
                    <div className="flex items-center justify-center flex-1 py-2.5">
                      <div className="flex items-center text-success-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="ml-2 text-sm font-medium">Verified</span>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2.5 text-sm border rounded-lg"
                  >
                    Resend
                  </button>
                </div>

                {showMobileOtp && (
                  <div className="bg-slate-50 p-3 rounded">
                    <p className="text-sm text-slate-700">We sent an OTP to <strong>{mobilePhone}</strong>. Enter it below.</p>
                    <div className="mt-2 flex gap-2">
                      <div className="relative flex-1">
                        <input
                          value={mobileOtp}
                          onChange={(e) => setMobileOtp(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyMobileOtp() } }}
                          className="input w-full py-2 text-sm pr-10"
                          placeholder="Enter mobile OTP"
                          disabled={isMobileVerified}
                        />
                        {isMobileVerified && (
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center text-success-600">
                            <CheckCircle className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      {!isMobileVerified && (
                        <button onClick={handleVerifyMobileOtp} disabled={!mobileOtp || isLoading} className="btn btn-primary px-4">Verify</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default ForgotPasswordPage
